import React from 'react';

import { cn } from '../../lib/utils';

import {
  Dialog as ShadDialog,
  DialogContent,
  DialogTitle,
} from './shadcn/dialog';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
  variant?: 'dialog' | 'fullscreen';
  centered?: boolean;
  backdrop?: 'static' | boolean;
  children: React.ReactNode;
  className?: string;
  id?: string;
  scrollable?: boolean;
}

interface ModalHeaderProps {
  children: React.ReactNode;
  className?: string;
  toggle?: () => void;
  id?: string;
}

interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
}

const sizeClasses = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  '6xl': 'sm:max-w-6xl',
  '7xl': 'sm:max-w-7xl',
  full: 'sm:max-w-full mx-4',
};

const isFlatpickrInteraction = (target: EventTarget | null, originalEvent?: PointerEvent): boolean => {
  const pathTargets = typeof originalEvent?.composedPath === 'function'
    ? originalEvent.composedPath()
    : [];

  return [target, ...pathTargets].some((node) => (
    node instanceof HTMLElement && Boolean(node.closest('.flatpickr-calendar'))
  ));
};

export function Modal({
  isOpen,
  onClose,
  title,
  size = 'xl',
  variant = 'fullscreen',
  centered = false,
  backdrop = true,
  children,
  className = '',
  id = undefined,
  scrollable = false,
}: ModalProps) {
  const preventClose = backdrop === 'static';

  return (
    <ShadDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (!preventClose) onClose();
        }
      }}
    >
      <DialogContent
        className={cn(
          'flex w-screen flex-col overflow-hidden gap-0 p-0',
          variant === 'fullscreen'
            ? 'top-0 left-1/2 h-[100dvh] w-screen max-h-[100dvh] max-w-[100vw] -translate-x-1/2 translate-y-0 rounded-none border-0 sm:max-w-[640px]'
            : 'max-h-[90dvh]',
          variant === 'dialog' ? sizeClasses[size] : '',
          centered || variant === 'fullscreen' ? '' : 'top-[5vh] sm:top-[10vh] translate-y-0',
          className,
        )}
        aria-describedby={undefined}
        onEscapeKeyDown={(e) => {
          if (preventClose) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isFlatpickrInteraction(e.target, e.detail.originalEvent)) {
            e.preventDefault();
            return;
          }
          if (preventClose) e.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">{title || 'Modal Dialog'}</DialogTitle>
        {title ? (
          <div className="flex-shrink-0 border-b border-border px-[1rem] py-[0.75rem]">
            <div className="text-md font-semibold">{title}</div>
          </div>
        ) : null}
        <div
          id={id}
          className={cn(
            'min-h-0 flex flex-1 flex-col overflow-y-auto [&>.amz-modal-body]:min-h-0 [&>.amz-modal-body]:flex-1 [&>.amz-modal-body]:overflow-y-auto [&>.amz-modal-footer]:shrink-0 [&>.amz-modal-header]:shrink-0 [&>*:not(.amz-modal-header):not(.amz-modal-body):not(.amz-modal-footer):not(.modal-header):not(.modal-body):not(.modal-footer)]:px-[1.5rem] [&>*:not(.amz-modal-header):not(.amz-modal-body):not(.amz-modal-footer):not(.modal-header):not(.modal-body):not(.modal-footer)]:py-[1rem]',
            scrollable ? 'overscroll-contain' : '',
          )}
        >
          {children}
        </div>
      </DialogContent>
    </ShadDialog>
  );
}

export function ModalHeader({ children, id = '', className = '', toggle }: ModalHeaderProps) {
  return (
    <div id={id} className={cn('amz-modal-header flex flex-shrink-0 items-center justify-between border-b border-border px-[1.5rem] py-[1rem]', className)}>
      <h5 className="modal-title text-lg font-medium">{children}</h5>
      {toggle && (
        <button
          type="button"
          className="btn-close"
          onClick={toggle}
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      )}
    </div>
  );
}

export function ModalBody({ children, id = '', className = '' }: ModalBodyProps) {
  return <div id={id} className={cn('amz-modal-body min-h-0 flex-1 overflow-y-auto px-[1.5rem] py-[1rem]', className)}>{children}</div>;
}

export function ModalFooter({ children, id = '', className = '' }: ModalFooterProps) {
  return (
    <div id={id} className={cn('amz-modal-footer flex flex-shrink-0 items-center justify-end gap-2 border-t border-border px-[1.5rem] py-[1rem]', className)}>
      {children}
    </div>
  );
}
